import React, { ChangeEvent, forwardRef, useCallback, useState } from "react";
import classNames, { Argument } from "classnames/dedupe";
import { v5 as uuidv5 } from "uuid";

import { ClearSearchIcon, SearchIcon } from "../../../../icons";
import SvgIcon from "../../../Icons/SvgIcon";
import useWatchState from "../../../../hooks/useWatchState";
import useEventListener from "../../../../hooks/useEventListener";
import { DEFAULT_UID_NAMESPACE } from "../../../../helpers";

export interface ISearchInputProps {
  /** input id */
  id?: string;
  /** Searchable text */
  searchTerm?: string;
  classes?: {
    root?: Argument;
    input?: Argument;
  };
  /** input autocomplete prop */
  autoComplete?: string;
  /** Placeholder of input */
  placeholderLabel?: string;
  /** Forces to focus on the forwarded ref */
  onForceBlur?(): void;
  onChange?(value: string): void;
  onFocus?(event: FocusEvent): void;
  onBlur?(event?: FocusEvent): void;
  onSearchSubmit?(value: string): void;
  onClear?: (event: React.MouseEvent) => void;
}

const SearchInput = forwardRef<HTMLInputElement, ISearchInputProps>(
  (
    {
      id,
      searchTerm,
      classes,
      autoComplete = "off",
      onChange,
      onFocus,
      onBlur,
      onForceBlur,
      onSearchSubmit,
      onClear,
      placeholderLabel = "Search",
    },
    ref,
  ) => {
    const [value, setValue] = useWatchState({
      value: searchTerm,
    });

    const [currentId] = useState(
      () => id || uuidv5("search", DEFAULT_UID_NAMESPACE),
    );

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        event.stopPropagation();
        setValue(newValue);
        onChange?.(newValue);
      },
      [onChange, setValue],
    );

    const handleClear = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        setValue("");
        onChange?.("");
        onClear?.(event);
      },
      [onChange, setValue, onClear],
    );

    const handleFocus = useCallback((event) => onFocus?.(event), [onFocus]);
    const handleBlur = useCallback((event) => onBlur?.(event), [onBlur]);
    const handleSubmit = useCallback(
      () => onSearchSubmit?.(value),
      [onSearchSubmit, value],
    );

    /**
     * Hotkey support (ESC)
     */
    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onForceBlur?.();
        }
      },
      [onForceBlur],
    );

    useEventListener<HTMLInputElement>("keydown", handleKeyDown);

    return (
      <label
        htmlFor={currentId}
        className={classNames(
          "d-flex align-items-center flex-grow-1 justify-content-stretch",
          classes?.root,
        )}
      >
        <SvgIcon
          icon={SearchIcon}
          classes={{ root: "icon-search" }}
          data-testid="SearchIcon"
        />
        <input
          id={currentId}
          data-testid="search-input-component"
          type="text"
          name="search"
          className={classNames("search-input", classes?.input)}
          placeholder={placeholderLabel}
          ref={ref}
          value={value}
          autoComplete={autoComplete}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onSubmit={handleSubmit}
          onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent)}
        />
        {value?.length > 0 && (
          <div
            data-testid="SearchInput__clear"
            className="icon-wrapper cursor-pointer me-3"
            onClick={handleClear}
          >
            <SvgIcon
              icon={ClearSearchIcon}
              classes={{ root: "icon-clear-search" }}
            />
          </div>
        )}
      </label>
    );
  },
);

export default SearchInput;
